// The CPU half of one per-cell data plane, written once for both backends.
//
// A plane is one typed array and one dirty flag: a sovereignty flip writes
// one byte and marks the plane, and the frame loop uploads the whole plane
// once. None of that names a graphics API, so `gl/texture.ts` and
// `gpu/data-texture.ts` extend this class and add the texture and the upload.

import type { DataFormat } from './types';

/** How many channels one texel of a format carries. */
export function channelsOf(format: DataFormat): number {
  if (format === 'rgba16f') return 4;
  return format === 'rg16f' ? 2 : 1;
}

/** Is the format an integer format? Integer textures can never be LINEAR. */
function isIntegerFormat(format: DataFormat): boolean {
  return format === 'r8ui';
}

export abstract class CellPlane {
  readonly data: Uint8Array | Float32Array;
  readonly channels: number;
  protected dirty = true;
  /**
   * The rows a `markRows` asked for since the last upload, inclusive, while
   * no other write marked the whole plane (R2897): -1 for the whole plane.
   * An upload copies these rows alone.
   */
  protected rowLo = -1;
  protected rowHi = -1;

  constructor(
    readonly name: string,
    readonly width: number,
    readonly height: number,
    readonly format: DataFormat,
    /** LINEAR is only legal on a float format. */
    readonly linear: boolean,
  ) {
    this.channels = channelsOf(format);
    const n = Math.max(1, width * height * this.channels);
    this.data = format === 'r8ui' || format === 'r8'
      ? new Uint8Array(n)
      : new Float32Array(n);
    if (linear && isIntegerFormat(format)) {
      throw new Error(`fast: ${name} asks for LINEAR on an integer format`);
    }
  }

  /** The index of cell (col,row) in `data`, first channel. */
  index(col: number, row: number): number {
    return (row * this.width + col) * this.channels;
  }

  /** Writes one channel-0 value and marks the plane dirty. */
  setCell(col: number, row: number, v: number): void {
    if (col < 0 || row < 0 || col >= this.width || row >= this.height) return;
    this.data[this.index(col, row)] = v;
    this.markDirty();
  }

  /** Writes both channels of a two-channel plane. */
  setCell2(col: number, row: number, r: number, g: number): void {
    if (col < 0 || row < 0 || col >= this.width || row >= this.height) return;
    const i = this.index(col, row);
    this.data[i] = r;
    this.data[i + 1] = g;
    this.markDirty();
  }

  /**
   * Replaces the whole plane. A short source leaves the tail as it was.
   *
   * The typed-array copy is the fast path and it is the one the terrain
   * prep needs: the distance field is 3.5 M floats for the core world, and
   * an element-by-element loop over it costs about ten milliseconds.
   */
  set(src: ArrayLike<number>): void {
    if (src.length <= this.data.length) {
      this.data.set(src, 0);
    } else {
      const n = this.data.length;
      for (let i = 0; i < n; i++) this.data[i] = src[i];
    }
    this.markDirty();
  }

  fill(v: number): void {
    this.data.fill(v);
    this.markDirty();
  }

  /** The whole plane uploads on the next frame. */
  markDirty(): void {
    this.dirty = true;
    this.rowLo = -1;
    this.rowHi = -1;
  }

  /**
   * Rows `r0` to `r1` alone upload on the next frame (R2897), unless a
   * write already marked the whole plane. A caller that wrote outside the
   * rows must call `markDirty`.
   */
  markRows(r0: number, r1: number): void {
    const lo = Math.max(0, Math.min(r0, r1)), hi = Math.min(this.height - 1, Math.max(r0, r1));
    if (hi < lo) return;
    if (!this.dirty) {
      this.dirty = true;
      this.rowLo = lo;
      this.rowHi = hi;
    } else if (this.rowLo >= 0) {
      this.rowLo = Math.min(this.rowLo, lo);
      this.rowHi = Math.max(this.rowHi, hi);
    }
  }

  /** The first row the next upload copies. */
  protected get upRow(): number { return this.rowLo >= 0 ? this.rowLo : 0; }

  /** How many rows the next upload copies. */
  protected get upRows(): number { return this.rowLo >= 0 ? this.rowHi - this.rowLo + 1 : this.height; }

  /** The upload landed: nothing is dirty. */
  protected uploaded(): void {
    this.dirty = false;
    this.rowLo = -1;
    this.rowHi = -1;
  }
  get isDirty(): boolean { return this.dirty; }
}
